import React, { useState } from 'react'
import { useEffectOnce } from 'hooks'
import { UPDATE_PLUS_NOTES } from 'constants/authorization'
import PropTypes from 'prop-types'
import HasRights from 'components/HasRights'
import { ReactComponent as SortUpIcon } from 'img/sort_up.svg'
import { ReactComponent as SortDownIcon } from 'img/sort_down.svg'
import { ASC, DESC } from 'utils/constants'
import strings from 'utils/strings'
import { getElapsedTime } from 'utils/utils'
import { createNote } from 'actions/feature/notes'
import { useDispatch } from 'react-redux'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import { Paragraph } from 'components/patterns'
import AddNote from './AddNote'

import {
  NotesHeader,
  NotesList,
  NotesListItem,
  NotesPostedTimeString,
  NotesSection,
  NotesSectionHeader,
  NotesString,
  NotesTitle,
  NotesTitleWrapper,
  SortButton,
  StyledNoteCounter,
} from './Notes.style'

const Notes = ({
  id,
  className,
  notes,
  getNotes,
  readOnly,
  showReadOnlyWarning,
  subUri,
  subType,
  subDescription,
  defaultNote = '',
  readOnlyWarning,
  onChange = () => {},
}) => {
  const dispatch = useDispatch()
  const [sortOrder, setSortOrder] = useState(DESC)
  useEffectOnce(getNotes)
  const getSortedComments = (comments, order) =>
    comments.sort((a, b) => {
      if (order === DESC) {
        return new Date(b.date) - new Date(a.date)
      }
      return new Date(a.date) - new Date(b.date)
    })
  const sortHandler = () => {
    setSortOrder(sortOrder === ASC ? DESC : ASC)
  }
  const noteHandler = (note) => {
    dispatch(createNote({ id, note, subUri, subType, subDescription }))
  }

  const changeHandler = (note) => {
    onChange(subUri, note)
  }

  const sortText =
    sortOrder === DESC
      ? strings.comments_sort_by_newest_first
      : strings.comments_sort_by_oldest_first
  const SortIcon = sortOrder === DESC ? SortUpIcon : SortDownIcon
  return (
    <div className={className}>
      {readOnly === false && (
        <HasRights rights={[UPDATE_PLUS_NOTES]}>
          <StyledCreateNote size="small">
            Create a note for{' '}
            {subDescription ? `"${subDescription}"` : `"${strings.feature}"`}
            {'.'}
          </StyledCreateNote>
          <AddNote
            onSubmit={noteHandler}
            onChange={changeHandler}
            defaultNote={defaultNote}
          />
        </HasRights>
      )}
      {showReadOnlyWarning && readOnly && readOnlyWarning && (
        <StyledCreateNote size="small">
          { readOnlyWarning }
        </StyledCreateNote>
      )}
      <NotesSection>
        <NotesSectionHeader>
          <StyledNoteCounter size={notes.length} showIcon={false} />
          <SortButton
            type="tertiary"
            onClick={sortHandler}
            disabled={notes?.length === 0}
          >
            <span>{sortText}</span>
            <SortIcon />
          </SortButton>
        </NotesSectionHeader>
        <NotesList>
          {getSortedComments(notes, sortOrder).map((item) => (
            <NotesListItem key={item.id}>
              <NotesHeader>
                <NotesTitleWrapper>
                  <NotesTitle>{item.author}</NotesTitle>
                  <NotesPostedTimeString>
                    {strings.comment_posted_time_string(
                      getElapsedTime(item.date)
                    )}
                  </NotesPostedTimeString>
                  <NoteTagChip>
                    {item.subDescription || strings.feature}
                  </NoteTagChip>
                </NotesTitleWrapper>
              </NotesHeader>
              <NotesString>{item.eventNotes}</NotesString>
            </NotesListItem>
          ))}
        </NotesList>
      </NotesSection>
    </div>
  )
}

Notes.defaultProps = {
  notes: [],
  showReadOnlyWarning: false,
  readOnlyWarning: null,
  defaultNote: '',
  onChange: () => {},
}

Notes.propTypes = {
  /** Classname */
  className: PropTypes.string,
  notes: PropTypes.array,
  createNote: PropTypes.func,
  getNotes: PropTypes.func,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  subUri: PropTypes.string,
  subType: PropTypes.string,
  subDescription: PropTypes.string,
  showReadOnlyWarning: PropTypes.bool,
  defaultNote: PropTypes.string,
  onChange: PropTypes.func,
  readOnlyWarning: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.element]),
}

export default Notes

export const NoteTagChip = styled.span(
  (props) => `
    border: 1px solid ${themeColor('solidGrey', props)};
    padding: 2px 4px;
    border-radius: 4px;
    margin-bottom: 8px;
    font-size: 12px;
    margin-left: 8px;
    color: ${themeColor('solidGrey', props)};
    position: absolute;
    right: 0;
    max-width: 50%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  `
)

const StyledCreateNote = styled(Paragraph)`
  margin-bottom: 10px;
`
